import * as React from 'react';

import {
  enumUnit,
  PurchaseLineItemType,
  Unit,
} from '@rematter/frontend-lib-api-client';
import { enumLoadWeightType } from '@rematter/modules/trade/types/load';
import {
  cn,
  Col,
  ConsoleTouchDialog,
  Grid,
  humanReadableShortPricingUnit,
  Row,
  TouchNumberField,
  TouchTextField,
} from '@rematter/ui-web';
import { useAtom, useSetAtom } from 'jotai';
import { LucideTag } from 'lucide-react';
import { v4 as uuidv4 } from 'uuid';

import type { NormalizedLineItem } from '../_types';
import { useDialogModalStyle } from '../hooks/useDialogModalStyle';

import { TouchDialogHeader } from './TouchDialogHeader';

import {
  $closeMixedDetails,
  $mixedDetailsState,
} from '~/modules/trade/_state/materialsInterface.state';
import { useTranslations } from '~/modules/trade/i18n/useTranslations';
import { toWeight } from '~/modules/trade/utils/weight';
import { useTheme } from '~/shared/hooks';

type MixedMaterialView = 'details' | 'photos' | 'files';

type EditMixedMaterialDialogProps = {
  lineItem: NormalizedLineItem;
  childItems: NormalizedLineItem[];
  onUpdateItem: (id: string, updates: Partial<NormalizedLineItem>) => void;
  onAddItem: (
    type: PurchaseLineItemType,
    data?: Partial<NormalizedLineItem>,
  ) => void;
  onDeleteItem: (id: string) => void;
  defaultUnit?: Unit;
  className?: string;
};

//
// ⚛️ ⚛️ ⚛️ EDIT MIXED MATERIAL DIALOG ⚛️ ⚛️ ⚛️
//
export const EditMixedMaterialDialog = ({
  lineItem,
  childItems,
  onUpdateItem,
  onAddItem,
  onDeleteItem,
  defaultUnit = enumUnit.Pounds,
  className,
}: EditMixedMaterialDialogProps) => {
  // Translations
  const t = useTranslations();
  const mixedDetails = t.scaleTicketPro.mixedMaterialDetails;

  // States
  const [view, setView] = React.useState<MixedMaterialView>('details');

  // Atoms
  const [mixedDetailsState, setMixedDetailsState] = useAtom($mixedDetailsState);
  const closeMixedDetails = useSetAtom($closeMixedDetails);
  const { isOpen, weights, notes, breakdown } = mixedDetailsState;

  // Other data hooks
  const { effectiveColorScheme } = useTheme();

  const modalStyle = useDialogModalStyle();

  const parsedGross = weights?.[enumLoadWeightType.Gross]?.value ?? 0;
  const parsedTare = weights?.[enumLoadWeightType.Tare]?.value ?? 0;
  const parsedNet = parsedGross - parsedTare;

  // Callbacks
  const applyChanges = React.useCallback(
    (overrides: {
      grossValue?: number;
      tareValue?: number;
      notes?: string;
    }) => {
      const g = overrides.grossValue ?? parsedGross;
      const ta = overrides.tareValue ?? parsedTare;
      const net = g - ta;

      onUpdateItem(lineItem.id, {
        weights: {
          [enumLoadWeightType.Gross]: g ? toWeight(g, defaultUnit) : null,
          [enumLoadWeightType.Tare]: ta ? toWeight(ta, defaultUnit) : null,
          [enumLoadWeightType.Net]: net > 0 ? toWeight(net, defaultUnit) : null,
        },
        notes: (overrides.notes ?? notes) || null,
      });

      const existingChildMap = new Map(childItems.map((c) => [c.itemId, c]));
      const newBreakdownIds = new Set(breakdown.map((b) => b.itemId));

      breakdown.forEach((b) => {
        const childNet = (net * b.percentage) / 100;
        const childNetWeight =
          childNet > 0 ? toWeight(childNet, defaultUnit) : null;
        const existingChild = existingChildMap.get(b.itemId);

        if (existingChild) {
          onUpdateItem(existingChild.id, {
            weights: {
              ...existingChild.weights,
              [enumLoadWeightType.Net]: childNetWeight,
            },
          });
        } else {
          onAddItem('Raw', {
            id: `temp-${uuidv4()}`,
            itemId: b.itemId,
            parentLineItemId: lineItem.id,
            weights: {
              [enumLoadWeightType.Net]: childNetWeight,
            },
            unitPrice: null,
            pricingUnit: defaultUnit,
            notes: t.scaleTicketPro.addMixedMaterial.total(b.percentage),
          });
        }
      });

      childItems.forEach((child) => {
        if (child.itemId && !newBreakdownIds.has(child.itemId)) {
          onDeleteItem(child.id);
        }
      });
    },
    [
      breakdown,
      parsedGross,
      parsedTare,
      defaultUnit,
      notes,
      lineItem.id,
      childItems,
      onUpdateItem,
      onAddItem,
      onDeleteItem,
    ],
  );

  const firstChild = breakdown?.[0];
  const extraChildCount = breakdown?.length > 1 ? breakdown?.length - 1 : 0;

  const tabs: MixedMaterialView[] = ['details', 'photos', 'files'];

  return (
    <ConsoleTouchDialog
      touchModeEnabled={true}
      effectiveColorScheme={effectiveColorScheme}
      isOpen={isOpen}
      onOpenChange={closeMixedDetails}
      className={cn('@EditMixedMaterialDialog', className)}
      dialogClassName="h-full w-full max-w-full"
      classNameHeader="!pb-0"
      contentStyle={modalStyle}
      header={
        <TouchDialogHeader
          icon={<LucideTag size={16} />}
          iconColor="primary"
          title={t.scaleTicketPro.inventoryItemField.fixedMixedMaterial}
          titleClassName="max-w-16 leading-tight"
          tabs={tabs.map((tab) => ({
            key: tab,
            label: mixedDetails.tabs[tab],
          }))}
          selectedTab={view}
          onTabChange={(key) => setView(key as MixedMaterialView)}
          extraHeaderContent={
            firstChild ? (
              <div className="h-[50px] min-w-40 rounded-md bg-white p-1">
                <Row yCenter gap2 className="h-full">
                  <Col>
                    <span className="text-xs text-neutral/40">
                      {firstChild.itemGroup}
                    </span>
                    <span className="text-sm">{firstChild.itemName}</span>
                  </Col>
                  {extraChildCount > 0 && (
                    <span className="flex h-full items-center rounded-sm bg-neutral/10 px-2 text-sm text-neutral/60">
                      +{extraChildCount}
                    </span>
                  )}
                </Row>
              </div>
            ) : undefined
          }
        />
      }
    >
      {view === 'details' && (
        <Col gap4>
          <Grid cols3 gap4>
            <Col gap1>
              <span className="text-xs font-medium text-neutral/60">
                {t.scaleTicketPro.columns.grossWeight}
              </span>
              <TouchNumberField
                aria-label={t.scaleTicketPro.columns.grossWeight}
                value={parsedGross}
                onChange={(v) => {
                  setMixedDetailsState((prev) => ({
                    ...prev,
                    weights: {
                      ...prev.weights,
                      [enumLoadWeightType.Gross]: toWeight(v, defaultUnit),
                    },
                  }));
                  applyChanges({ grossValue: v });
                }}
                formatOptions={{
                  minimumFractionDigits: 0,
                  maximumFractionDigits: 2,
                }}
                touchModeEnabled
                suffix={humanReadableShortPricingUnit(defaultUnit)}
                className="h-16 text-2xl"
              />
            </Col>
            <Col gap1>
              <span className="text-xs font-medium text-neutral/60">
                {t.scaleTicketPro.columns.tareWeight}
              </span>
              <TouchNumberField
                aria-label={t.scaleTicketPro.columns.tareWeight}
                value={parsedTare}
                onChange={(v) => {
                  setMixedDetailsState((prev) => ({
                    ...prev,
                    weights: {
                      ...prev.weights,
                      [enumLoadWeightType.Tare]: toWeight(v, defaultUnit),
                    },
                  }));
                  applyChanges({ tareValue: v });
                }}
                formatOptions={{
                  minimumFractionDigits: 0,
                  maximumFractionDigits: 2,
                }}
                touchModeEnabled
                suffix={humanReadableShortPricingUnit(defaultUnit)}
                className="h-16 text-2xl"
              />
            </Col>
            <Col gap1>
              <span className="text-xs font-medium text-neutral/60">
                {t.scaleTicketPro.columns.netWeight}
              </span>
              <TouchNumberField
                aria-label={t.scaleTicketPro.columns.netWeight}
                value={parsedNet > 0 ? parsedNet : 0}
                isDisabled
                formatOptions={{
                  minimumFractionDigits: 0,
                  maximumFractionDigits: 2,
                }}
                touchModeEnabled
                suffix={humanReadableShortPricingUnit(defaultUnit)}
                className="h-16 text-2xl"
              />
            </Col>
          </Grid>

          <TouchTextField
            label={t.scaleTicketPro.freightDetails.fields.notes}
            placeholder={t.scaleTicketPro.addMixedMaterial.notesPlaceholder}
            value={notes}
            onChange={(v) => {
              setMixedDetailsState((prev) => ({ ...prev, notes: v }));
              applyChanges({ notes: v });
            }}
            touchModeEnabled
            rows={4}
          />
        </Col>
      )}

      {view === 'photos' && (
        <Row xCenter yCenter className="h-full text-sm text-neutral/50">
          {mixedDetails.noPhotos}
        </Row>
      )}

      {view === 'files' && (
        <Row xCenter yCenter className="h-full text-sm text-neutral/50">
          {mixedDetails.noFiles}
        </Row>
      )}
    </ConsoleTouchDialog>
  );
};
